<template>
    <div class="full-width" style="height: calc(100vh - 60px);">
        <q-scroll-area :thumb-style="thumbStyle" :horizontal-offset="[0, 4]" class="full-width q-px-md q-pt-md" style="height: calc(100vh - 60px);">
            <div class="flex full-width items-end">
                <div class="text-bold" style="font-size: 14px;">Objectives</div>
                <span class="q-ml-md" style="color: #858585; font-size: 12px;">{{ statusSummary }}{{ runContext }}</span>
            </div>

            <div v-if="loadingBcos" class="flex full-width justify-center items-center q-mt-xl">
                <q-spinner color="accent" size="20px" class="q-mr-md" />
                <div style="font-size: 12px; color: #5f5f5f;">Loading objectives...</div>
            </div>

            <div v-else-if="!objectives.length" class="flex full-width justify-center q-mt-xl">
                <div class="text-center">
                    <div style="color: #414141; font-size: 16px; font-weight: 600;">Nothing has been run yet</div>
                    <div class="q-mt-sm" style="color: #858585; font-size: 12px; line-height: 1.45;">
                        Pick an objective on the Strategize page and review &amp; approve it —<br>
                        the agent's reasoning and its recommendations will show up here.
                    </div>
                </div>
            </div>

            <template v-else>
                <template v-if="runningBcos.length">
                    <div class="flex full-width items-end justify-between q-mt-md q-mb-xs">
                        <div class="text-secondary" style="font-size: 10px; font-weight: 700; letter-spacing: 1px;">RUNNING</div>
                        <div class="q-ml-md" style="color: #858585; font-size: 10px;">{{ runningBcos.length }} objective{{ runningBcos.length === 1 ? '' : 's' }}</div>
                    </div>
                    <q-separator color="accent" size="2px" />

                    <RunningBcoCard
                        v-for="bco in runningBcos"
                        :key="`running-bco-${bco.bco_id}`"
                        :bco="bco"
                        :expanded="expandedBcoId === bco.bco_id"
                        @toggle="toggleBco(bco.bco_id)"
                        @toggle-trace="toggleTrace(bco)"
                        @send-for-mlr-approval="sendForMlrApproval(bco)"
                        @start-activity="startActivity(bco)"
                    />
                </template>

                <template v-if="finishedBcos.length">
                    <div class="flex full-width items-end justify-between q-mt-md q-mb-xs">
                        <div class="text-grey-7" style="font-size: 10px; font-weight: 700; letter-spacing: 1px;">COMPLETED</div>
                        <div class="q-ml-md" style="color: #858585; font-size: 10px;">{{ finishedBcos.length }} objective{{ finishedBcos.length === 1 ? '' : 's' }}</div>
                    </div>
                    <q-separator color="grey-5" size="2px" />

                    <RunningBcoCard
                        v-for="bco in finishedBcos"
                        :key="`finished-bco-${bco.bco_id}`"
                        :bco="bco"
                        :expanded="expandedBcoId === bco.bco_id"
                        @toggle="toggleBco(bco.bco_id)"
                        @toggle-trace="toggleTrace(bco)"
                        @send-for-mlr-approval="sendForMlrApproval(bco)"
                        @start-activity="startActivity(bco)"
                    />
                </template>

                <div class="full-width q-py-md"></div>
            </template>
        </q-scroll-area>
    </div>
</template>

<script>
import APIService from '../../APIService.js';
import RunningBcoCard from '@/components/RunningBcoCard.vue';
import { getTriggeredBcoIds, pruneTriggeredBcoIds } from '@/triggeredBcos.js';
import { publishBcoRunStatus } from '@/bcoRunStatus.js';

const TRACE_POLL_INTERVAL_MS = 5000;
const TRACE_STEP_REVEAL_DELAY_MS = 1000;
// A run the user has just approved is marked as started in BCO_MASTER before the trigger responds,
// but the navigation that follows can still outrun the write becoming visible to the next read, so
// a run that was triggered but is not in the list yet is waited for rather than given up on.
const MISSING_RUN_RETRIES = 4;
const MISSING_RUN_RETRY_DELAY_MS = 750;
// The server approves a BCO a few seconds after it is sent for MLR approval, so a waiting BCO is
// checked often enough for the approval to show up close to when it lands.
const MLR_POLL_INTERVAL_MS = 2000;

export default {
    name: 'NBAWorkbenchView',

    components: {
        RunningBcoCard,
    },

    data() {
        return {
            thumbStyle: {
                borderRadius: '7px',
                backgroundColor: '#E5E8ED',
                width: '7px',
                opacity: 0.99
            },

            objectives: [],
            loadingBcos: false,
            expandedBcoId: null,
            sessionBcoIds: [],
            tracePollTimer: null,
            mlrPollTimer: null,
            mlrPollInFlight: false,
            isFirstActivation: true,
        };
    },

    computed: {
        // Runs triggered on this visit lead the list, since that is what the user came here to watch.
        runningBcos() {
            return this.objectives
                .filter((bco) => this.isRunning(bco))
                .sort((first, second) => Number(this.triggeredThisSession(second)) - Number(this.triggeredThisSession(first)));
        },

        finishedBcos() {
            return this.objectives.filter((bco) => !this.isRunning(bco));
        },

        statusSummary() {
            const parts = [];
            if (this.runningBcos.length) {
                parts.push(`${this.runningBcos.length} running`);
            }
            if (this.finishedBcos.length) {
                parts.push(`${this.finishedBcos.length} completed`);
            }
            return parts.join(' · ') || 'Nothing run yet';
        },

        runContext() {
            const first = this.objectives[0];
            return first ? ` · ${first.cycle} · ${first.brand}` : '';
        },
    },

    mounted() {
        this.fetchBcos();
    },

    // The view is kept alive behind the other tabs, so opening it again has to pick up the runs
    // that were triggered, and the ones that finished, while it was off screen. `mounted` already
    // covers the first visit, and it runs before the first `activated`.
    activated() {
        if (this.isFirstActivation) {
            this.isFirstActivation = false;
            return;
        }

        this.fetchBcos();
    },

    beforeUnmount() {
        this.stopTracePolling();
        this.stopMlrPolling();
        this.objectives.forEach((bco) => clearTimeout(bco.reveal_timer));
    },

    methods: {
        isRunning(bco) {
            return bco.running_status === 'running';
        },

        triggeredThisSession(bco) {
            return this.sessionBcoIds.includes(String(bco.bco_id));
        },

        toggleBco(bcoId) {
            this.expandedBcoId = this.expandedBcoId === bcoId ? null : bcoId;

            const bco = this.objectives.find((objective) => objective.bco_id === bcoId);
            if (bco && this.expandedBcoId === bcoId) {
                this.fetchSampleDecisions(bco);
            }
        },

        toggleTrace(bco) {
            bco.show_trace = !bco.show_trace;
        },

        // Everything the card keeps for itself, as opposed to what the server sends for the run.
        blankObjective() {
            return {
                received_step_count: 0,
                pending_steps: [],
                visible_steps: [],
                reveal_timer: null,
                run_complete: false,
                trace_complete: false,
                // The trace is the agent's working, not its answer, so a card opens on the
                // decisions unless the user is here to watch the run happen.
                show_trace: false,
                decisions_requested: false,
                loading_decisions: false,
                sample_decisions: [],
                total_decisions: 0,
                veeva_header: null,
                veeva_message: null,
                sending_for_mlr: false,
                starting_activity: false,
            };
        },

        // The trigger marks the run as started before it responds, so a BCO the user has approved
        // counts as on the workbench only once that write is on the list it reads back.
        hasStarted(bcos, bcoId) {
            return bcos.some((bco) => String(bco.bco_id) === bcoId && this.isRunning(bco));
        },

        async loadWorkbenchBcos() {
            const awaitedBcoIds = getTriggeredBcoIds();
            let bcos = [];

            for (let attempt = 0; attempt <= MISSING_RUN_RETRIES; attempt += 1) {
                if (attempt) {
                    await new Promise((resolve) => setTimeout(resolve, MISSING_RUN_RETRY_DELAY_MS));
                }

                const response = await APIService.post(`${process.env.VUE_APP_ROOT_API}/common/running-bcos`, {});
                bcos = response.data || [];

                if (awaitedBcoIds.every((id) => this.hasStarted(bcos, id))) {
                    break;
                }
            }

            return bcos;
        },

        async fetchBcos() {
            const isFirstLoad = !this.objectives.length;
            // An approved run is what the user is waiting on, so the list sits behind the loader
            // until it is on it rather than showing the state the user has just left behind.
            this.loadingBcos = isFirstLoad || !getTriggeredBcoIds().every((id) => this.hasStarted(this.objectives, id));
            try {
                const bcos = await this.loadWorkbenchBcos();

                this.sessionBcoIds = pruneTriggeredBcoIds(bcos.filter((bco) => this.isRunning(bco)).map((bco) => bco.bco_id));

                // Re-opening the tab refreshes the list in place, so a card that is already on
                // screen keeps the trace it has revealed and the decisions it has loaded.
                const onScreen = new Map(this.objectives.map((bco) => [bco.bco_id, bco]));
                const freshRunIds = new Set();

                this.objectives = bcos.map((bco) => {
                    const current = onScreen.get(bco.bco_id);
                    // Approving a BCO that has already run clears its trace in BCO_MASTER and its
                    // decisions are rewritten only when the agent finishes, so the card drops what
                    // the previous run left on it instead of carrying it into this one.
                    const isRerun = Boolean(current) && this.isRunning(bco) && !this.isRunning(current);
                    if (isRerun) {
                        clearTimeout(current.reveal_timer);
                    }

                    if (!current || isRerun) {
                        freshRunIds.add(bco.bco_id);
                    }

                    const objective = Object.assign(current && !isRerun ? current : this.blankObjective(), bco);

                    // A run the user has just approved lands here to be watched, so its reasoning
                    // is on screen from the start rather than behind the Show trace button.
                    if (freshRunIds.has(bco.bco_id) && this.triggeredThisSession(objective)) {
                        objective.show_trace = true;
                    }

                    return objective;
                });

                // A run approved on the Strategize page is what the user came here to watch.
                const justTriggered = this.runningBcos.find((bco) => this.triggeredThisSession(bco) && freshRunIds.has(bco.bco_id));
                const expandedIsGone = this.expandedBcoId !== null && !this.objectives.some((bco) => bco.bco_id === this.expandedBcoId);
                if (justTriggered) {
                    this.expandedBcoId = justTriggered.bco_id;
                } else if (isFirstLoad || expandedIsGone) {
                    this.expandedBcoId = (this.runningBcos[0] || this.finishedBcos[0])?.bco_id ?? null;
                }

                // Objectives that were already running before this session, and those that have already
                // finished, get their existing trace in one go; only the runs triggered here play out
                // step by step. Steps already on screen are never revealed twice.
                this.objectives.forEach((bco) => {
                    bco.run_complete = !this.isRunning(bco);
                    this.ingestThoughtTrace(bco, bco.thought_trace, !this.triggeredThisSession(bco));
                    this.markTraceCompleteIfRevealed(bco);
                    publishBcoRunStatus(bco.bco_id, bco.running_status);
                });

                const expandedBco = this.objectives.find((bco) => bco.bco_id === this.expandedBcoId);
                if (expandedBco) {
                    this.fetchSampleDecisions(expandedBco);
                }

                this.startTracePolling();
                this.startMlrPolling();
            } catch (error) {
                console.error(error);
                this.$q.notify({
                    message: 'Error fetching objectives',
                    color: 'negative',
                    icon: 'error',
                    position: 'top-right',
                    timeout: 3000,
                });
            } finally {
                this.loadingBcos = false;
            }
        },

        startTracePolling() {
            this.stopTracePolling();
            if (this.objectives.some((bco) => this.isRunning(bco))) {
                this.tracePollTimer = setInterval(this.pollThoughtTraces, TRACE_POLL_INTERVAL_MS);
            }
        },

        stopTracePolling() {
            clearInterval(this.tracePollTimer);
            this.tracePollTimer = null;
        },

        // The agent owns running_status in BCO_MASTER, so that — not the trace itself — is what says
        // a run is over.
        async pollThoughtTraces() {
            const stillRunning = this.objectives.filter((bco) => this.isRunning(bco));
            if (!stillRunning.length) {
                this.stopTracePolling();
                return;
            }

            await Promise.all(
                stillRunning.map(async (bco) => {
                    try {
                        const response = await APIService.post(`${process.env.VUE_APP_ROOT_API}/common/bco-thought-trace`, { bco_id: bco.bco_id });
                        this.ingestThoughtTrace(bco, response.data.thought_trace);
                        this.applyRunStatus(bco, response.data);
                    } catch (error) {
                        console.error(error);
                    }
                }),
            );
        },

        applyRunStatus(bco, payload) {
            bco.running_status = payload.running_status;
            publishBcoRunStatus(bco.bco_id, bco.running_status);

            if (this.isRunning(bco)) {
                return;
            }

            // The HCP and decision counts only settle once the agent has written its output, so the
            // poll that sees the run finish is the one that carries them.
            bco.hcp_count = payload.hcp_count ?? bco.hcp_count;
            bco.activity_status = payload.activity_status ?? bco.activity_status;
            bco.started_at = payload.started_at ?? bco.started_at;
            bco.started_by = payload.started_by ?? bco.started_by;
            bco.decision_count = payload.decision_count ?? bco.decision_count;
            bco.run_complete = true;

            this.markTraceCompleteIfRevealed(bco);
            if (this.expandedBcoId === bco.bco_id) {
                this.fetchSampleDecisions(bco);
            }
        },

        ingestThoughtTrace(bco, trace, revealInstantly = false) {
            const steps = Array.isArray(trace) ? trace : [];
            if (steps.length <= bco.received_step_count) {
                return;
            }

            const newSteps = steps.slice(bco.received_step_count);
            bco.received_step_count = steps.length;

            if (revealInstantly) {
                bco.visible_steps.push(...newSteps);
                this.markTraceCompleteIfRevealed(bco);
                return;
            }

            bco.pending_steps.push(...newSteps);
            this.revealNextStep(bco);
        },

        revealNextStep(bco) {
            // A pending timer means a reveal cycle is already draining the queue.
            if (bco.reveal_timer) {
                return;
            }

            const step = bco.pending_steps.shift();
            if (!step) {
                this.markTraceCompleteIfRevealed(bco);
                return;
            }

            bco.visible_steps.push(step);

            // The trace is only settled on the tick after its last step, so the step the run ends
            // on gets the same beat on screen as every step before it.
            bco.reveal_timer = setTimeout(() => {
                bco.reveal_timer = null;
                this.revealNextStep(bco);
            }, TRACE_STEP_REVEAL_DELAY_MS);
        },

        // The trace is only done once the run has ended and every step it produced is on screen.
        markTraceCompleteIfRevealed(bco) {
            if (!bco.run_complete || bco.pending_steps.length || bco.trace_complete) {
                return;
            }

            bco.trace_complete = true;

            // Its working done, the trace folds away and leaves the decisions in view; the Show
            // trace button brings it back. A run that failed is the exception, because there the
            // reasoning is all the card has to show.
            if (bco.running_status !== 'failed') {
                bco.show_trace = false;
            }
        },

        mlrStatusOf(bco) {
            return String(bco.mlr_status || '').trim().toLowerCase();
        },

        async sendForMlrApproval(bco) {
            if (bco.sending_for_mlr) {
                return;
            }

            bco.sending_for_mlr = true;
            try {
                const response = await APIService.post(`${process.env.VUE_APP_ROOT_API}/common/send-bco-for-mlr-approval`, { bco_id: bco.bco_id });
                bco.mlr_status = response.data.mlr_status;
                this.startMlrPolling();
            } catch (error) {
                console.error(error);
                this.$q.notify({
                    message: 'Error sending this objective for MLR approval',
                    color: 'negative',
                    icon: 'error',
                    position: 'top-right',
                    timeout: 3000,
                });
            } finally {
                bco.sending_for_mlr = false;
            }
        },

        async startActivity(bco) {
            if (bco.starting_activity) {
                return;
            }

            bco.starting_activity = true;
            try {
                const response = await APIService.post(`${process.env.VUE_APP_ROOT_API}/common/start-bco-activity`, { bco_id: bco.bco_id });
                bco.activity_status = response.data.activity_status;
                bco.started_at = response.data.started_at;
                bco.started_by = response.data.started_by;
            } catch (error) {
                console.error(error);
                this.$q.notify({
                    message: "Error starting this objective's activity",
                    color: 'negative',
                    icon: 'error',
                    position: 'top-right',
                    timeout: 3000,
                });
            } finally {
                bco.starting_activity = false;
            }
        },

        startMlrPolling() {
            if (this.mlrPollTimer || !this.objectives.some((bco) => this.mlrStatusOf(bco) === 'waiting_for_approval')) {
                return;
            }
            this.mlrPollTimer = setInterval(this.pollMlrStatuses, MLR_POLL_INTERVAL_MS);
        },

        stopMlrPolling() {
            clearInterval(this.mlrPollTimer);
            this.mlrPollTimer = null;
        },

        async pollMlrStatuses() {
            const waiting = this.objectives.filter((bco) => this.mlrStatusOf(bco) === 'waiting_for_approval');
            if (!waiting.length) {
                this.stopMlrPolling();
                return;
            }

            // A Snowflake read can outlast the interval, and overlapping polls would notify twice.
            if (this.mlrPollInFlight) {
                return;
            }

            this.mlrPollInFlight = true;
            try {
                await Promise.all(
                    waiting.map(async (bco) => {
                        try {
                            const response = await APIService.post(`${process.env.VUE_APP_ROOT_API}/common/bco-mlr-status`, { bco_id: bco.bco_id });
                            bco.mlr_status = response.data.mlr_status;

                            if (this.mlrStatusOf(bco) === 'approved') {
                                this.$q.notify({
                                    message: `MLR approved for "${bco.name}"`,
                                    color: 'positive',
                                    icon: 'check_circle',
                                    position: 'top-right',
                                    timeout: 3000,
                                });
                            }
                        } catch (error) {
                            console.error(error);
                        }
                    }),
                );
            } finally {
                this.mlrPollInFlight = false;
            }
        },

        async fetchSampleDecisions(bco) {
            if (!bco.run_complete || bco.running_status === 'failed' || bco.decisions_requested) {
                return;
            }

            bco.decisions_requested = true;
            bco.loading_decisions = true;
            try {
                const response = await APIService.post(`${process.env.VUE_APP_ROOT_API}/common/bco-sample-decisions`, { bco_id: bco.bco_id });
                bco.sample_decisions = response.data.decisions || [];
                bco.total_decisions = response.data.total_decisions || 0;
                bco.veeva_header = response.data.veeva_header || null;
                bco.veeva_message = response.data.veeva_message || null;
            } catch (error) {
                console.error(error);
            } finally {
                bco.loading_decisions = false;
            }
        },
    },
};
</script>
